'use client';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import type { NodeProps } from '@xyflow/react';
import { Handle, Position } from '@xyflow/react';
import type { Logger } from '@core/be/platform/logging/Logger';
import { LuPlus, LuMinus } from 'react-icons/lu';
import { Button } from '@/core/ui/primitives/button';
import type { MindmapLayoutDirection, MindmapNodeShape } from '@/features/mindmap/core/MindmapTypes';
import type { MindmapNodeData } from '@/features/mindmap/core/MindmapTransform';
import { MindmapNodeContextMenu } from './MindmapContextMenus';
const BASE_NODE_CLASS =
  'relative min-h-[28px] min-w-[210px] px-2 py-1 inline-flex items-center justify-start text-xs font-normal tracking-tight transition-all duration-50 backdrop-blur-3xl';
const SHAPE_CONTAINER_CLASSES: Record<MindmapNodeShape, string> = {
  // Geometry only — no color styling
  rounded: `${BASE_NODE_CLASS} rounded-[6px] border`,
  rectangle: `${BASE_NODE_CLASS} rounded-[0px] border`,
  pill: `${BASE_NODE_CLASS} rounded-[50px] border`,
  underline: `${BASE_NODE_CLASS} rounded-[0px] border !border-l-transparent !border-t-transparent !border-r-transparent !px-1 !pl-2`,
};
const SHAPE_SELECTED_CLASSES: Record<MindmapNodeShape, string> = {
  rounded: 'ring-2 ring-offset-2 ring-offset-white/68 ring-sky-300/52 dark:ring-offset-white/12 dark:ring-sky-400/42',
  rectangle: 'ring-2 ring-offset-2 ring-offset-white/68 ring-sky-300/52 dark:ring-offset-white/12 dark:ring-sky-400/42',
  pill: 'ring-2 ring-offset-2 ring-offset-white/70 ring-emerald-300/52 dark:ring-offset-white/12 dark:ring-emerald-400/42',
  underline: 'ring-2 ring-offset-2 ring-offset-white/68 ring-sky-300/52 dark:ring-offset-white/12 dark:ring-sky-400/42',
};
const DEFAULT_TEXT_CLASS = 'text-gray-900 dark:text-gray-100';
const DEFAULT_BG_CLASS = 'bg-white dark:bg-gray-900';
const DEFAULT_BORDER_CLASS = 'border-gray-300 dark:border-gray-700';
export interface RuntimeMindmapNodeData extends MindmapNodeData {
  isEditing?: boolean;
  dndEnabled?: boolean;
  onCommitRename?: (id: string, value: string) => void;
  onCancelRename?: () => void;
  initialEditValue?: string;
  shouldSkipCommit?: () => boolean;
  onHoverChange?: (hovered: boolean) => void;
  logger?: Logger;
  registerEditRef?: (element: HTMLInputElement | HTMLTextAreaElement | null) => void;
  selectionMode?: 'select-all' | 'caret-end';
  isRoot?: boolean;
  hasChildren?: boolean;
  onToggleCollapse?: (id: string, next: boolean) => void;
  onNodeStylePreview?: (
    patch: Partial<Pick<MindmapNodeData, 'textColor' | 'bgColor' | 'borderColor' | 'shape'>>
  ) => void;
  onNodeStyleCommit?: (
    patch: Partial<Pick<MindmapNodeData, 'textColor' | 'bgColor' | 'borderColor' | 'shape'>>
  ) => void;
  onNodeDirectionPreview?: (direction: MindmapLayoutDirection) => void;
  onNodeDirectionCommit?: (direction: MindmapLayoutDirection) => void;
  onNodeDirectionReset?: () => void;
  selectedId?: string;
  selectedStyle?: Partial<Pick<MindmapNodeData, 'textColor' | 'bgColor' | 'borderColor' | 'shape'>>;
  disabledToolbar?: boolean;
  isDraggingToolbar?: boolean;
  viewportZoom?: number;
  onContextMenuOpen?: () => void;
  // Canvas pan suppression hooks for the small window before dnd-kit "drag start"
  onDragCandidatePointerDown?: () => void;
  onDragCandidatePointerUp?: () => void;
  onCreateSiblingAfter?: () => void;
  onCreateSiblingBefore?: () => void;
  onCreateChildFirst?: () => void;
  onCreateChildLast?: () => void;
  onChangeBgColor?: (color: string) => void;
  onChangeBorderColor?: (color: string) => void;
  onChangeShape?: (shape: MindmapNodeShape) => void;
  onChangeDirection?: (direction: MindmapLayoutDirection) => void;
  onCopyNode?: () => void;
  onPasteNode?: () => void;
  onPasteSubtree?: () => void;
  onPasteStyle?: () => void;
  canPaste?: boolean;
}
const MindmapNode = memo((props: any) => {
  const { data: rawData, selected } = props;
  const data = rawData as RuntimeMindmapNodeData;
  const {
    id,
    text,
    isEditing,
    dndEnabled,
    onCommitRename,
    onCancelRename,
    initialEditValue,
    shouldSkipCommit,
    onHoverChange,
    logger,
    registerEditRef,
    selectionMode,
    isRoot,
    hasChildren,
    onNodeStylePreview,
    onNodeStyleCommit,
    onNodeDirectionPreview,
    onNodeDirectionCommit,
    onNodeDirectionReset,
    selectedId,
    selectedStyle,
    disabledToolbar,
    isDraggingToolbar,
    onContextMenuOpen,
    onCreateSiblingAfter,
    onCreateSiblingBefore,
    onCreateChildFirst,
    onCreateChildLast,
    onChangeBgColor,
    onChangeBorderColor,
    onChangeShape,
    onChangeDirection,
    onCopyNode,
    onPasteNode,
    onPasteSubtree,
    onPasteStyle,
    canPaste,
    viewportZoom,
    onToggleCollapse,
  } = data;
  const isGhost = !!data.isGhost;
  const draggableEnabled = !!dndEnabled && !isRoot && !isEditing && !isGhost;
  // Set up drag & drop functionality
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: id,
    disabled: !draggableEnabled,
  });
  const shape: MindmapNodeShape = (data.shape ?? 'rounded') as MindmapNodeShape;
  const baseClass = SHAPE_CONTAINER_CLASSES[shape] ?? SHAPE_CONTAINER_CLASSES.rounded;
  const selectedClass = selected && !isGhost ? SHAPE_SELECTED_CLASSES[shape] ?? '' : '';
  const direction: MindmapLayoutDirection = (data.layoutDirection ?? 'right') as MindmapLayoutDirection;
  const handlePositions = useMemo<Record<'source' | 'target', Position>>(() => {
    switch (direction) {
      case 'left':
        return { source: Position.Left, target: Position.Right };
      case 'up':
        return { source: Position.Top, target: Position.Bottom };
      case 'down':
        return { source: Position.Bottom, target: Position.Top };
      case 'right':
      default:
        return { source: Position.Right, target: Position.Left };
    }
  }, [direction]);
  const { source: sourcePosition, target: targetPosition } = handlePositions;
  const handleStyleOverrides = useMemo<Record<'source' | 'target', React.CSSProperties | undefined>>(() => {
    if (shape !== 'underline') {
      return { source: undefined, target: undefined };
    }
    const styleFor = (position: Position): React.CSSProperties | undefined => {
      switch (position) {
        case Position.Left:
          return {
            top: 'auto',
            bottom: '0px',
            transform: 'translate(-50%, 50%)',
          };
        case Position.Right:
          return {
            top: 'auto',
            bottom: '0px',
            transform: 'translate(50%, 50%)',
          };
        default:
          return undefined;
      }
    };
    return {
      source: styleFor(sourcePosition),
      target: styleFor(targetPosition),
    };
  }, [shape, sourcePosition, targetPosition]);
  const inlineStyle = useMemo<React.CSSProperties>(() => {
    const style: React.CSSProperties = {};
    if (data.bgColor) style.backgroundColor = data.bgColor;
    if (data.textColor) style.color = data.textColor;
    if (data.borderColor) style.borderColor = data.borderColor;
    if (data.borderStyle) style.borderStyle = data.borderStyle;
    if (typeof data.borderWidth === 'number') style.borderWidth = data.borderWidth;
    if (data.borderRadius) style.borderRadius = data.borderRadius;
    if (data.fontSize) style.fontSize = data.fontSize;
    if (data.lineHeight) style.lineHeight = data.lineHeight;
    if (data.boxShadow) style.boxShadow = data.boxShadow;
    if (isGhost) {
      style.color = 'transparent';
      style.backgroundColor = 'rgba(59,130,246,0.12)';
      style.borderColor = 'rgba(59,130,246,0.65)';
      style.boxShadow = '0 0 0 2px rgba(59,130,246,0.25)';
    }
    return style;
  }, [
    data.bgColor,
    data.borderColor,
    data.borderStyle,
    data.borderWidth,
    data.borderRadius,
    data.textColor,
    data.fontSize,
    data.lineHeight,
    data.boxShadow,
    isGhost,
  ]);
  // Simplified: no local hover-driven toolbar visibility.
  // We keep lightweight hover notifications for lasso/UX gating elsewhere.
  const invZoom = useMemo(() => {
    const z = typeof viewportZoom === 'number' && viewportZoom > 0 ? viewportZoom : 1;
    return 1 / z;
  }, [viewportZoom]);
  // No per-node toolbar anymore; keep invZoom for other UI elements
  const collapsed = (data as any).collapsed ?? false;
  const collapseButtonStyle = useMemo<React.CSSProperties | null>(() => {
    if (!hasChildren && !collapsed) return null;
    const gap = 8; // px - distance from node edge
    const baseStyle: React.CSSProperties = {
      position: 'absolute',
      transform: `scale(${invZoom})`,
      pointerEvents: 'auto',
      zIndex: 10,
    };
    switch (direction) {
      case 'left':
        return shape === 'underline'
          ? {
              ...baseStyle,
              right: '100%',
              top: '100%', // align to bottom border
              transformOrigin: 'right center',
              // place beside node horizontally, vertically centered on bottom edge
              transform: `translateX(-${gap}px) translateY(-50%) scale(${invZoom})`,
            }
          : {
              ...baseStyle,
              right: '100%',
              top: '50%',
              transformOrigin: 'right center',
              transform: `translateX(-${gap}px) translateY(-50%) scale(${invZoom})`,
            };
      case 'up':
        return {
          ...baseStyle,
          left: '50%',
          bottom: '100%',
          transformOrigin: 'center bottom',
          transform: `translateX(-50%) translateY(-${gap}px) scale(${invZoom})`,
        };
      case 'down':
        return {
          ...baseStyle,
          left: '50%',
          top: '100%',
          transformOrigin: 'center top',
          transform: `translateX(-50%) translateY(${gap}px) scale(${invZoom})`,
        };
      case 'right':
      default:
        return shape === 'underline'
          ? {
              ...baseStyle,
              left: '100%',
              top: '100%', // align to bottom border
              transformOrigin: 'left center',
              transform: `translateX(${gap}px) translateY(-50%) scale(${invZoom})`,
            }
          : {
              ...baseStyle,
              left: '100%',
              top: '50%',
              transformOrigin: 'left center',
              transform: `translateX(${gap}px) translateY(-50%) scale(${invZoom})`,
            };
    }
  }, [hasChildren, collapsed, direction, invZoom, data, shape]);
  // Ensure inline editor width grows to content so the node expands up to its max width.
  // We cap at 40ch to match the container's max-w-[40ch].
  const initialEditLen = (initialEditValue ?? text)?.length ?? 1;
  const initialWidthCh = Math.min(40, Math.max(1, initialEditLen));
  return (
    <MindmapNodeContextMenu
      isRoot={!!isRoot}
      onOpen={onContextMenuOpen}
      onCreateSiblingAfter={onCreateSiblingAfter}
      onCreateSiblingBefore={onCreateSiblingBefore}
      onCreateChildFirst={onCreateChildFirst}
      onCreateChildLast={onCreateChildLast}
      onChangeBgColor={onChangeBgColor}
      onChangeBorderColor={onChangeBorderColor}
      onChangeShape={onChangeShape}
      onChangeDirection={onChangeDirection}
      onPreviewStyle={onNodeStylePreview}
      currentBgColor={data.bgColor}
      currentBorderColor={data.borderColor}
      currentShape={data.shape as MindmapNodeShape | undefined}
      onCopyNode={onCopyNode}
      onPasteNode={onPasteNode}
      onPasteSubtree={onPasteSubtree}
      onPasteStyle={onPasteStyle}
      canPaste={canPaste}>
      <div
        ref={setNodeRef}
        {...(draggableEnabled ? listeners : {})}
        {...(draggableEnabled ? attributes : {})}
        data-testid='mindmap-node'
        data-node-id={id}
        onPointerDownCapture={() => {
          if (draggableEnabled) {
            data.onDragCandidatePointerDown?.();
          }
        }}
        onPointerUpCapture={() => {
          if (draggableEnabled) {
            data.onDragCandidatePointerUp?.();
          }
        }}
        onPointerCancelCapture={() => {
          if (draggableEnabled) {
            data.onDragCandidatePointerUp?.();
          }
        }}
        aria-selected={selected ? 'true' : 'false'}
        onMouseEnter={() => {
          if (!isEditing) {
            onHoverChange?.(true);
          }
        }}
        onMouseLeave={() => {
          onHoverChange?.(false);
        }}
        className={`group relative inline-flex min-w-[8rem] max-w-[40ch] items-center text-sm font-medium ${baseClass} ${selectedClass} ${(() => {
          const parts: string[] = [];
          if (!data.textColor) parts.push(DEFAULT_TEXT_CLASS);
          if (!data.bgColor && shape !== 'underline') parts.push(DEFAULT_BG_CLASS);
          if (!data.borderColor) parts.push(DEFAULT_BORDER_CLASS);
          if (draggableEnabled) parts.push('cursor-grab active:cursor-grabbing');
          if (isGhost)
            parts.push(
              'pointer-events-none border-2 border-dashed border-sky-400 bg-sky-100/70 text-transparent dark:border-sky-300/80 dark:bg-sky-900/40'
            );
          return parts.join(' ');
        })()}`}
        style={{
          ...inlineStyle,
          ...(draggableEnabled ? { touchAction: 'none' } : null),
        }}>
        {!isRoot && (
          <Handle type='target' position={targetPosition} isConnectable={false} style={handleStyleOverrides.target} />
        )}
        {(isRoot || hasChildren) && (
          <Handle type='source' position={sourcePosition} isConnectable={false} style={handleStyleOverrides.source} />
        )}
        {/* Drag handle removed: entire node is now draggable when enabled */}

        {/* Per-node style toolbar removed: now rendered globally in MindmapCanvas */}
        {isEditing ? (
          <textarea
            autoFocus
            defaultValue={initialEditValue ?? text}
            rows={1}
            className='block w-fit min-w-full max-w-[40ch] align-top rounded border-0 px-0 py-0 text-xs leading-tight font-normal bg-transparent focus:outline-none resize-none whitespace-pre-wrap break-words'
            style={{ ['fieldSizing' as any]: 'content' }}
            ref={registerEditRef}
            onFocus={(e) => {
              const el = e.currentTarget;
              try {
                // Selection behavior
                if (selectionMode === 'select-all') {
                  el.select();
                } else {
                  const end = el.value.length;
                  el.setSelectionRange(end, end);
                }
              } catch {}
              logger?.debug(
                'input focus: id=%s value=%s sel=[%d,%d] mode=%s',
                id,
                e.currentTarget.value,
                e.currentTarget.selectionStart ?? -1,
                e.currentTarget.selectionEnd ?? -1,
                selectionMode ?? 'n/a'
              );
              // No JS sizing; CSS manages size
            }}
            onBlur={(e) => {
              const skip = shouldSkipCommit?.() ?? false;
              logger?.debug('input blur: id=%s value=%s skip=%s', id, e.currentTarget.value, skip);
              if (skip) return;
              onCommitRename?.(id, e.currentTarget.value);
            }}
            onKeyDownCapture={(e) => {
              // Allow global audio transcription hotkey to pass through while editing
              const isMac = typeof navigator !== 'undefined' && /mac|ipad|iphone|ipod/i.test(navigator.platform ?? '');
              const activation = e.code === 'KeyR' && e.altKey && (isMac ? e.metaKey : e.ctrlKey);
              logger?.debug(
                'input keydown[capture]: id=%s key=%s value=%s repeat=%s',
                id,
                e.key,
                (e.target as HTMLInputElement).value,
                e.repeat
              );
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onCommitRename?.(id, (e.target as HTMLInputElement).value);
              } else if (e.key === 'Enter' && e.shiftKey) {
                // Allow newline insertion on Shift+Enter
                // Let default behavior insert newline, then auto-grow via onInput
              } else if (e.key === 'Escape') {
                e.preventDefault();
                onCancelRename?.();
              }
              // Do not block the global audio toggle (⌘/Ctrl + ⌥ + R)
              if (!activation) {
                e.stopPropagation();
              }
            }}
            onInput={undefined}
          />
        ) : isGhost ? (
          ''
        ) : (
          text
        )}
        {hasChildren && !(data as any).collapsed && (
          <Handle type='source' position={sourcePosition} style={handleStyleOverrides.source} />
        )}

        {!isGhost && collapseButtonStyle && (
          <Button
            type='button'
            variant='ghost'
            size='icon'
            aria-label={collapsed ? 'Expand children' : 'Collapse children'}
            title={collapsed ? 'Expand children' : 'Collapse children'}
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapse?.(id, !collapsed);
            }}
            onMouseEnter={(e) => e.stopPropagation()}
            onMouseLeave={(e) => e.stopPropagation()}
            style={collapseButtonStyle}
            className='flex h-3.5 w-3.5 items-center justify-center rounded-full border border-gray-300 bg-white/95 text-gray-700 shadow-sm backdrop-blur-sm hover:bg-primary/10 hover:border-primary/30 dark:border-gray-600 dark:bg-gray-900/90 dark:text-gray-200'>
            {collapsed ? <LuPlus className='h-3 w-3' aria-hidden /> : <LuMinus className='h-3 w-3' aria-hidden />}
          </Button>
        )}
      </div>
    </MindmapNodeContextMenu>
  );
});
MindmapNode.displayName = 'MindmapNode';
export default MindmapNode;
